/**
 * ALDER SEARCH — CPO Search Page
 * URL: /executive-search/cpo-search
 */

import { useEffect } from "react";
import { Link } from "wouter";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useFaqSchema } from "@/hooks/useFaqSchema";

const cpoSearchFaqs = [
  {
    question: "What does a CPO search for a VC- and PE-backed company involve?",
    answer: "A CPO search for a VC- and PE-backed company focuses on finding product leaders who can define and execute a product roadmap that directly supports the value creation plan. The CPO must be able to balance customer needs with investor return objectives and communicate product strategy clearly to a sponsor board.",
  },
  {
    question: "How is a CPO search different from a Head of Product search?",
    answer: "A CPO is a C-suite appointment with board-level accountability for product strategy and typically manages a product organisation of significant scale. A Head of Product is a senior leadership hire focused on execution within a defined product area. For VC- and PE-backed businesses, the distinction matters because the CPO is expected to represent product at investor level.",
  },
  {
    question: "How long does a retained CPO search take?",
    answer: "A retained CPO search for a VC- or PE-backed portfolio company typically delivers a shortlist in six to eight weeks and completes placement in ten to fourteen weeks. The timeline depends on the seniority of the role, the breadth of the market map, and notice periods.",
  },
  {
    question: "What makes a CPO candidate VC- and PE-ready?",
    answer: "A VC- and PE-ready CPO has experience making product investment decisions under capital constraints, can articulate product ROI in financial terms, and has managed a product organisation through a period of accelerated growth or transformation. Ideally they have operated in a VC- and PE-backed or venture-backed environment before.",
  },
  {
    question: "Do you run CPO searches for early-stage portfolio companies or only established businesses?",
    answer: "We run CPO searches across the full range of VC- and PE-backed portfolio company stages, from businesses at initial platform acquisition through to pre-exit scale-ups. The brief and candidate profile differ significantly by stage, which is why we invest time in understanding the investment thesis before writing any specification.",
  }
];


const steps = [
  {
    num: "01",
    title: "Product Strategy & Thesis Alignment",
    body: "Before we write a brief, we understand your product, your market, and what the CPO needs to deliver at this stage of ownership. Are you driving product-led growth? Consolidating a platform post-acquisition? Building the product story for exit? The brief is different in each case and we write it accordingly.",
  },
  {
    num: "02",
    title: "Ideal Candidate Profile",
    body: "We build the profile together. Not a job description -- a picture of the person. The product background, the commercial track record, the VC and PE experience required, and the leadership style that fits your engineering and commercial teams. A CPO who can't hold a board-level conversation about product economics is the wrong hire regardless of their roadmap credentials.",
  },
  {
    num: "03",
    title: "Full Market Map",
    body: "We map every viable CPO candidate against your profile. That includes people actively looking and -- more importantly -- those who aren't. We look specifically for candidates who've operated at the intersection of product strategy and commercial accountability. That's a narrower pool than it looks.",
  },
  {
    num: "04",
    title: "Discreet Outreach",
    body: "We approach candidates personally. The firm's identity is protected until we've established genuine interest and fit. No volume outreach. Every approach is individual and specific to the mandate.",
  },
  {
    num: "05",
    title: "VC- and PE-Specific Assessment",
    body: "Every shortlisted CPO goes through structured competency interviews, a VC- and PE-specific leadership assessment, and early-stage reference validation. We test for commercial instinct, board-level communication, and the ability to make product decisions fast under resource constraint. Vision is common. Accountability for outcomes is not.",
  },
  {
    num: "06",
    title: "Shortlist Presentation",
    body: "Three to five candidates. Each comes with a full written assessment, not a CV summary. You make the decision from this document -- not from the first interview.",
  },
  {
    num: "07",
    title: "Offer, Onboarding & Guarantee",
    body: "We manage the offer, navigate competing interests, and stay close through the first 90 days. The first quarter is when product direction gets set. Our placement retention guarantee reflects our confidence in every hire we make. If a placed hire leaves within 3 months, we rerun the search at no additional retainer.",
  },
];

const outcomes = [
  "A CPO assessed against your product thesis and exit timeline -- not a generic product leadership framework.",
  "Access to a network of VC- and PE-proven CPOs who've owned product strategy in sponsor-backed businesses before.",
  "A shortlist built from market coverage that goes beyond active candidates -- including those not looking.",
];

export default function CpoSearchPage() {
  useFaqSchema(cpoSearchFaqs, "cpo-faq-jsonld");
  useEffect(() => {
    const prev = document.title;
    document.title = "CPO Search for VC- and PE-Backed Companies | Alder Search";
    const meta = document.querySelector('meta[name="description"]');
    const prevContent = meta?.getAttribute("content") ?? "";
    meta?.setAttribute(
      "content",
      "Retained CPO search for VC and PE investors and portfolio companies. We place Chief Product Officers who own the product roadmap, drive commercial growth, and build for exit."
    );
    return () => {
      document.title = prev;
      meta?.setAttribute("content", prevContent);
    };
  }, []);

  const handleEngageClick = () => {
    const el = document.querySelector("#contact");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = "/#contact";
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "oklch(0.97 0.006 80)" }}>
      <Navigation />

      {/* HERO */}
      <section
        className="relative pt-40 pb-24 overflow-hidden"
        style={{
          background: "linear-gradient(160deg, oklch(0.18 0.055 155) 0%, oklch(0.245 0.065 155) 100%)",
        }}
      >
        <div className="container relative z-10">
          <div className="mb-4 flex items-center gap-2" style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.65rem", letterSpacing: "0.14em", textTransform: "uppercase" }}>
            <Link href="/" style={{ color: "oklch(0.55 0.02 155)", textDecoration: "none" }} onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.72 0.09 75)"; }} onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.55 0.02 155)"; }}>Home</Link>
            <span style={{ color: "oklch(0.38 0.07 155)" }}>›</span>
            <Link href="/executive-search" style={{ color: "oklch(0.55 0.02 155)", textDecoration: "none" }} onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.72 0.09 75)"; }} onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.55 0.02 155)"; }}>Executive Search</Link>
            <span style={{ color: "oklch(0.38 0.07 155)" }}>›</span>
            <span style={{ color: "oklch(0.62 0.085 75)" }}>CPO Search</span>
          </div>
          <h1
            className="mb-6"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 700,
              fontSize: "clamp(2.2rem, 5vw, 3.8rem)",
              lineHeight: 1.1,
              color: "oklch(0.97 0.006 80)",
              maxWidth: "700px",
            }}
          >
            The CPO Who Owns the Roadmap and Delivers the Returns.
          </h1>
          <p
            className="mb-10"
            style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontWeight: 300,
              fontSize: "clamp(1rem, 2vw, 1.15rem)",
              lineHeight: 1.75,
              color: "oklch(0.78 0.018 80)",
              maxWidth: "580px",
            }}
          >
            The CPO in a VC- and PE-backed business carries a different brief to any other product role. They own the product strategy, set the roadmap, and are accountable for the commercial outcomes that flow from it. Most product executives have led teams. Fewer have owned the P&L consequences. We find the ones who have.
          </p>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={handleEngageClick}
              className="px-8 py-3.5 font-ui text-sm font-600 transition-all duration-300"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                fontSize: "0.75rem",
                background: "oklch(0.62 0.085 75)",
                color: "oklch(0.18 0.055 155)",
                border: "none",
                borderRadius: "2px",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.72 0.09 75)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.62 0.085 75)";
              }}
            >
              Engage Alder Search
            </button>
            <Link
              href="/executive-search"
              className="px-8 py-3.5 font-ui text-sm font-500 transition-all duration-300"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontWeight: 500,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                fontSize: "0.75rem",
                background: "transparent",
                color: "oklch(0.88 0.012 80)",
                border: "1px solid oklch(0.45 0.07 155)",
                borderRadius: "2px",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = "oklch(0.62 0.085 75)";
                (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.62 0.085 75)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = "oklch(0.45 0.07 155)";
                (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.88 0.012 80)";
              }}
            >
              ← Back to Executive Search
            </Link>
          </div>
        </div>
      </section>

      {/* OUTCOMES STRIP */}
      <section
        className="py-14"
        style={{ background: "oklch(0.245 0.065 155)", borderBottom: "1px solid oklch(0.32 0.07 155)" }}
      >
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {outcomes.map((text, i) => (
              <div key={i} className="flex gap-4 items-start">
                <div
                  className="mt-1 shrink-0"
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    background: "oklch(0.62 0.085 75)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4L3.5 6.5L9 1" stroke="oklch(0.18 0.055 155)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <p
                  style={{
                    fontFamily: "'Source Serif 4', Georgia, serif",
                    fontWeight: 300,
                    fontSize: "0.92rem",
                    lineHeight: 1.65,
                    color: "oklch(0.82 0.015 80)",
                  }}
                >
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS SECTION */}
      <section className="py-24" style={{ background: "oklch(0.97 0.006 80)" }}>
        <div className="container">
          <div
            className="mb-3"
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "0.62rem",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "oklch(0.62 0.085 75)",
            }}
          >
            How This Search Works
          </div>
          <h2
            className="mb-5"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 700,
              fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
              lineHeight: 1.15,
              color: "oklch(0.245 0.065 155)",
              maxWidth: "600px",
            }}
          >
            A CPO Search Built Around Your Product and Commercial Thesis.
          </h2>
          <p
            className="mb-16"
            style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontWeight: 300,
              fontSize: "1rem",
              lineHeight: 1.75,
              color: "oklch(0.42 0.03 155)",
              maxWidth: "560px",
            }}
          >
            This isn't a search for a product manager who's been promoted. It's a search for a commercially-accountable product leader who can own the roadmap, build the team, and drive the outcomes your value creation plan depends on.
          </p>

          <div className="flex flex-col gap-0">
            {steps.map((step, idx) => (
              <div
                key={step.num}
                className="grid grid-cols-1 md:grid-cols-[80px_1fr] gap-4 py-8"
                style={{
                  borderTop: idx === 0 ? "1px solid oklch(0.88 0.01 80)" : "1px solid oklch(0.88 0.01 80)",
                }}
              >
                <div
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontWeight: 400,
                    fontSize: "0.72rem",
                    letterSpacing: "0.1em",
                    color: "oklch(0.62 0.085 75)",
                    paddingTop: "3px",
                  }}
                >
                  {step.num}
                </div>
                <div>
                  <h3
                    className="mb-2"
                    style={{
                      fontFamily: "'Playfair Display', Georgia, serif",
                      fontWeight: 600,
                      fontSize: "1.05rem",
                      color: "oklch(0.245 0.065 155)",
                    }}
                  >
                    {step.title}
                  </h3>
                  <p
                    style={{
                      fontFamily: "'Source Serif 4', Georgia, serif",
                      fontWeight: 300,
                      fontSize: "0.92rem",
                      lineHeight: 1.75,
                      color: "oklch(0.42 0.03 155)",
                    }}
                  >
                    {step.body}
                  </p>
                </div>
              </div>
            ))}
            <div style={{ borderTop: "1px solid oklch(0.88 0.01 80)" }} />
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section
        className="py-20"
        style={{ background: "oklch(0.18 0.055 155)" }}
      >
        <div className="container">
          <div
            className="mx-auto"
            style={{ maxWidth: "680px", textAlign: "center" }}
          >
            <div
              className="mb-6"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontStyle: "italic",
                fontSize: "clamp(1.15rem, 2.5vw, 1.55rem)",
                lineHeight: 1.6,
                color: "oklch(0.88 0.012 80)",
              }}
            >
              "A CPO who can build a great roadmap isn't hard to find. One who owns the commercial consequences of it is."
            </div>
            <div
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "0.65rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "oklch(0.62 0.085 75)",
              }}
            >
              Ryan Ollerenshaw, Founding Partner, Alder Search
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section
        id="contact"
        className="py-24"
        style={{ background: "oklch(0.245 0.065 155)" }}
      >
        <div className="container text-center">
          <h2
            className="mb-5"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 700,
              fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
              lineHeight: 1.15,
              color: "oklch(0.97 0.006 80)",
            }}
          >
            Ready to Discuss a CPO Mandate?
          </h2>
          <p
            className="mb-10 mx-auto"
            style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontWeight: 300,
              fontSize: "1rem",
              lineHeight: 1.75,
              color: "oklch(0.72 0.018 80)",
              maxWidth: "520px",
            }}
          >
            Whether you have an active CPO search, are scaling a product function post-acquisition, or want to understand what the right profile looks like at your portfolio company's stage -- we welcome a confidential conversation.
          </p>
          <button
            onClick={handleEngageClick}
            className="px-10 py-4 font-ui text-sm font-600 transition-all duration-300"
            style={{
              fontFamily: "'DM Sans', system-ui, sans-serif",
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              fontSize: "0.75rem",
              background: "oklch(0.62 0.085 75)",
              color: "oklch(0.18 0.055 155)",
              border: "none",
              borderRadius: "2px",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.72 0.09 75)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.62 0.085 75)";
            }}
          >
            Engage Alder Search
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
